import {
  createQueryService,
  createFormDataService,
  createMutationService,
  createBlobService,
} from "utils/factories";
import {
  CampaignActivityLogDTO,
  CampaignMaterialActivityLogDTO,
  CampaignMaterialType,
  CampaignShortDTO,
  CreateCampaignDTO,
} from "./campaigns.types";
import { PaginatedQueryResult } from "service/generic.types";

const moduleBasePath = "/api/order-windows";
export default class CampaignsService {
  static readonly getCampaigns = createMutationService<
    PaginatedQueryResult<CampaignShortDTO>,
    any
  >(
    ({ take, skip }) =>
      `${moduleBasePath}/get-filtered?take=${take || 10}&skip=${skip || 0}`
  );

  static readonly getCampaign = createQueryService<
    CampaignShortDTO,
    { id: string | number }
  >(({ id }) => `${moduleBasePath}/${id}`);

  static readonly createCampaign = createMutationService<
    CampaignShortDTO,
    CreateCampaignDTO
  >(`${moduleBasePath}/create`);

  static readonly updateCampaign = createMutationService<
    CampaignShortDTO,
    CreateCampaignDTO
  >(`${moduleBasePath}/update`, "PUT");

  static readonly getLocalizedChildMarketsForOW = createQueryService<
    { id: number; name: string }[]
  >(
    (params?: { id?: number }) =>
      `/api/order-windows/GetLocalizedChildMarketsForOW/${params?.id}`
  );

  static readonly publishCampaign = createMutationService<
    any,
    { campaignId: string | number }
  >(({ campaignId }) => `${moduleBasePath}/publish/${campaignId}`, "POST");

  static readonly exportCampaign = createBlobService<any, any>(
    (params) =>
      `/api/order-windows/export`,
    "POST"
  );

  static readonly closeCampaign = createMutationService<
    any,
    { campaignId: string | number }
  >(({ campaignId }) => `${moduleBasePath}/close/${campaignId}`, "POST");

  static readonly archiveCampaign = createMutationService<
    any,
    { campaignId: string | number }
  >(({ campaignId }) => `${moduleBasePath}/delete?id=${campaignId}`, "DELETE");

  static readonly confirmPrice = createMutationService<
    any,
    { campaignId: string | number }
  >(({ campaignId }) => `/api/order-windows/confirmprice/${campaignId}`, "POST");

  static readonly duplicateCampaign = createMutationService<
    any,
    { campaignId: string | number }
  >(
    ({ campaignId }) => `/api/order-windows/duplicate/${campaignId}`,
    "POST"
  );

  static readonly deleteCampaignMedia = createMutationService<
    any,
    { fileId: string }
  >(({ fileId }) => `${moduleBasePath}/files/${fileId}`, "DELETE");

  static readonly uploadCampaignMediaFromLink = createMutationService<
    any,
    { link: string; campaignId?: string }
  >(
    ({ link, campaignId }) =>
      `${moduleBasePath}/files/upload-file-from-link?link=${link}${campaignId ? `&orderWindowId=${campaignId}` : ""
      }`,
    "POST"
  );

  static readonly uploadCampaignMedia = createFormDataService<any, any>(
    ({ campaignId }) =>
      `${moduleBasePath}/files/upload-file?${campaignId ? `orderWindowId=${campaignId}` : ""
      }`,
    "POST"
  );

  static readonly getCampaignSubscribers = createQueryService<
    { name: string; email: string; campaignId: number }[],
    { campaignId: string }
  >(({ campaignId }) => `${moduleBasePath}/${campaignId}/subscribers`);

  static readonly subcribeMeToCampaign = createMutationService<
    never,
    { campaignId }
  >(
    ({ campaignId }) => `${moduleBasePath}/${campaignId}/subscribers/add/me`,
    "POST"
  );

  static readonly copyCampaign = createMutationService<never, { campaignId }>(
    ({ campaignId }) => `${moduleBasePath}/duplicate/${campaignId}`,
    "POST"
  );

  static readonly subcribeOthersToCampaign = createMutationService<
    never,
    {
      campaignId: string;
      subscribers: { name: string; email: string; orderWindowId: number }[];
    }
  >(
    ({ campaignId }) =>
      `${moduleBasePath}/${campaignId}/subscribers/add/others`,
    "POST"
  );

  static readonly unsubscribeMemberFromCampaign = createMutationService<
    never,
    { campaignId; email: string }
  >(
    ({ campaignId, email }) =>
      `${moduleBasePath}/${campaignId}/subscribers/${email}`,
    "DELETE"
  );

  static readonly getCampaignActivityLog = createQueryService<
    CampaignActivityLogDTO[],
    { campaignId: string | number }
  >(({ campaignId }) => `${moduleBasePath}/${campaignId}/logs`);

  static readonly getCampaignMaterialsActivityLog = createQueryService<
    CampaignMaterialActivityLogDTO[],
    { campaignId: string | number }
  >(
    ({ campaignId }) =>
      `/api/order-window-materials/${campaignId}/logs-by-order-window`
  );

  static readonly createCampaignMaterial = createMutationService<
    any,
    CampaignMaterialType
  >("/api/order-window-materials/create", "POST");

  static readonly getCampaignMaterials = createQueryService<
    { data: CampaignMaterialType[]; total: number; take: number; skip: number },
    { orderWindowId: number; take: number; skip: number }
  >(
    ({ orderWindowId }) =>
      `/api/order-window-materials/get-materials-by-order-window`
  );

  static readonly updateCampaignMaterial = createMutationService<
    any,
    CampaignMaterialType
  >("/api/order-window-materials/update", "PUT");

  static readonly duplicateCampaignMaterial = createMutationService<
    any,
    { materialId: number | string }
  >(
    ({ materialId }) =>
      `/api/order-window-materials/duplicate?materialId=${materialId}`,
    "POST"
  );

  static readonly deleteCampaignMaterial = createMutationService<
    any,
    { id: number }
  >(({ id }) => `/api/order-window-materials/delete?id=${id}`, "DELETE");

  static readonly deleteCampaignMaterialMedia = createMutationService<
    any,
    { fileId: string }
  >(({ fileId }) => `/api/order-window-materials/files/${fileId}`, "DELETE");

  static readonly uploadCampaignMaterialMediaFromLink = createMutationService<
    any,
    { link: string; campaignMaterialId?: string }
  >(
    ({ link, campaignMaterialId }) =>
      `/api/order-window-materials/files/upload-file-from-link?link=${link}${campaignMaterialId ? `&orderWindowMaterialId=${campaignMaterialId}` : ""
      }`,
    "POST"
  );

  static readonly uploadCampaignMaterialMedia = createFormDataService<
    any,
    {
      campaignMaterialId: string | number;
      documentId?: string | number;
      file: File;
    }
  >(
    ({ campaignMaterialId, documentId }) =>
      `/api/order-window-materials/files/upload-file?${campaignMaterialId ? `orderWindowMaterialId=${campaignMaterialId}` : ""
      }${documentId ? `&documentId=${documentId}&isQuality=true` : ""}`,
    "POST"
  );

  static readonly requestMaterialQuantity = createMutationService<
    any,
    {
      orderWindowMaterialId: number;
      quantity: number;
      market: {
        id: number;
        name: string;
      };
      // requestor: string;
      comments: string;
      // incoterm: number;
      mediaFiles: {
        fileId: number;
      }[];
    }
  >("/api/order-window-materials/request-quantity");

  static readonly getQuantityRequests = createQueryService<any, any>(
    ({ id }) => `/api/order-window-materials/request-quantity/${id}`
  );

 static readonly getVendorsByOrderWindowAndMarket = createQueryService<
  {
    vendorCode: string;
    vendorName: string;
  }[],
  {
    orderWindowId: number;
    marketId: number;
  }
>(
  () => `/api/order-windows/GetVendorsByOrderWindowAndMarket`
);

  static readonly editQuantityRequest = createMutationService<
    never,
    { id: number; quantity: number }
  >(`/api/order-window-materials/request-quantity/`, "PUT");

  static readonly adjustQuantityRequest = createMutationService<
  any,
  {
    id: number;
    quantity: number;
    incoTermId: number;
    comments: string;
    quantityUnits: string;
    materialName: string;
    adjustedPrice: number | null;
    adjustedCurrencyCode?: string;
    vendorCode?: string;
    vendorName?: string;
  }
>(
  `/api/order-window-materials/request-quantityv2/`,
  "PUT"
);

  // static readonly adjustQuantityRequest = createMutationService<
  //   any,
  //   {
  //     id: number;
  //     quantity: number;
  //     incoTermId: number;
  //     comments: string;
  //     quantityUnits: string;
  //     materialName: string;
  //     adjustedPrice: number | null;
  //     currencyCode?: string;
  //     vendorCode?: string;
  //     vendorName?: string;
  //   }
  // >(
  //   `/api/order-window-materials/request-quantityv2/`,
  //   "PUT"
  // );

 static readonly deleteQuantityRequest = async ({
  id,
}: {
  id: number;
}) => {
  const response = await fetch(
    `/api/order-window-materials/request-quantity/${id}`,
    {
      method: "DELETE",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to delete quantity request");
  }

  return response;
};

  static readonly exportQuantityConsolidation = createBlobService<
    any,
    { id: string | number }
  >(
    ({ id }) => `/api/order-window-materials/request-quantity/export/${id}`,
    "GET"
  );

  static readonly uploadQuantityRequestMedia = createFormDataService<
    any,
    { file: File; materialQuantityRequestId?: string | number }
  >("/api/quantity-requests/files/upload-file", "POST");

  static readonly deleteQuantityRequestMedia = createMutationService<
    never,
    { fileId: string | number }
  >(
    ({ fileId }) => `/api/order-window-materials/request-quantity/${fileId}`,
    "DELETE"
  );

  static readonly submitCampaign = createMutationService<
    never,
    { campaignId: string | number }
  >(({ campaignId }) => `${moduleBasePath}/submit/${campaignId}`, "POST");

  //   static readonly getTasksByOrderWindowId = createMutationService<
  //   any,
  //   { orderWindowId: number }
  // >(
  //   () =>
  //     `/api/HackathonPsl/GetPslEntriesV2?take=10&skip=0`,
  //   "POST"
  // );
  static readonly getTasksByOrderWindowId = async ({
    orderWindowId,
    take = 10,
    skip = 0,
  }: {
    orderWindowId: number;
    take?: number;
    skip?: number;
  }) => {
    return createMutationService<any, any>(
      () => `/api/HackathonPsl/GetPslEntriesV2?take=${take}&skip=${skip}`,
      "POST"
    )({
      orderWindowTask: true,
      spendCatL1s: [],
      spendCatL2s: [],
      spendCatL3s: [],
      companies: [],
      markets: [],
      statuses: [],
      complianceStatuses: [],
      suppliers: [],
      materials: [],
      businessUnits: [],
      strategicBuyers: [],
      pslStatuses: [],
      supplierDiversity: [],
      wayOfBuying: [],
      scope: [],
      time: null,
      exactId: null,
      taskKind: null,
      createdByUserId: null,
      fromSpend: "",
      toSpend: "",
      childrenOf: null,
      currencySpend: "",
      plants: [],
      orderBy: {
        field: "CreatedOn",
        direction: "desc",
      },
      pslCreationAndValidityDate: null,
      zones: [],
      pslIds: [],
      orderWindowIds: [
        {
          id: String(orderWindowId),
        },
      ],
      pslSearch: null,
      materialSearch: null,
      showObsolete: false,
      favoriteSwitchOn: false,
      supplierDescription: [],
      catalogType: [
        {
          id: 1,
          title: "All Catalogues",
        },
      ],
      promoCategories: [],
      searchTerm: null,
      take,
      skip,
    });
  };
}
